/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { OrderTrackingMessage } from '../configuration/anonymous-delivery';
import {
    DELIVERY_INFORMATION_LEG_STATES,
    DELIVERY_INFORMATION_LEG_TYPES,
    DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS
} from '../configuration/delivery-information';
import {
    AnonymousDeliveryStepStatus,
    IAnonymousDelivery,
    IAnonymousDeliveryEvent,
    IAnonymousDeliveryMainCarriage,
    IAnonymousDeliveryStep
} from '../modules/lxs-anonymous-delivery/mocks/lxs-anonymous-delivery.mock-data';

export interface IPostAnonymousDeliveryResponse {
    success: boolean;
    message?: string;
    deliveries?: IAnonymousDelivery[];
}

export interface IOrderTrackingEvent {
    Id: number;
    Name: string;
    Timestamp: string;
    Location?: string;
    ExtensionProperties: [];
}

export interface IOrderTrackingLegs {
    Id: number;
    LegType: string;
    ModeOfTransportation: string;
    State?: string;
    BlNumber: string;
    CarrierName: string;
    Container: string;
    ContainerType: string;
    ShipFlightNumber: string;
    Events?: IOrderTrackingEvent[];
    ExtensionProperties: [];
}

export interface IOrderTrackingDelivery {
    EstimatedArrivalDate?: string;
    DeliveryNumber?: string;
    LiveTrackingAvailable: boolean;
    LiveTrackingUrl?: string;
    Legs?: IOrderTrackingLegs[];
}

export interface IPostAnonymousDeliveryData {
    '@odata.context': string;
    OrderNumber?: string;
    CustomerPONumber?: string;
    ConfirmedDate?: string;
    Message: OrderTrackingMessage;
    OrderTrackingData?: IOrderTrackingDelivery[];
}

/**
 * PostAnonymousDelivery Input Action
 */
export class PostAnonymousDeliveryInput implements Msdyn365.IActionInput {
    public trackingId: string;
    public postalCode: string;
    constructor(trackingId: string, postalCode: string) {
        this.trackingId = trackingId;
        this.postalCode = postalCode;
    }
    public getCacheKey = () => `PostAnonymousDelivery-${this.trackingId}-${this.postalCode}`;
    public getCacheObjectType = () => 'PostAnonymousDelivery';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostAnonymousDeliveryInput('', '');
};

interface ISyntheticLeg {
    Id: number;
    ModeOfTransportation: string;
    Events: IOrderTrackingEvent[];
}

const createSyntheticLeg = (id: number, eventName: string, icon: string, timestamp?: string): ISyntheticLeg => ({
    Id: id,
    ModeOfTransportation: icon,
    Events: [{ Id: id, Name: eventName, Timestamp: timestamp ?? '', Location: '', ExtensionProperties: [] }]
});

const legIcon = (leg: { ModeOfTransportation?: string }): string => {
    const mode = leg.ModeOfTransportation?.toUpperCase() ?? '';
    // eslint-disable-next-line security/detect-object-injection
    return DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS[mode] || 'truck';
};

const findCurrentActiveLeg = (legs: IOrderTrackingLegs[]): IOrderTrackingLegs | undefined => {
    if (legs.length === 0) {
        return undefined;
    }
    const activeOrCompletedLegs = legs.filter(
        leg => leg.State === DELIVERY_INFORMATION_LEG_STATES.Active || leg.State === DELIVERY_INFORMATION_LEG_STATES.Completed
    );
    return activeOrCompletedLegs.length > 0 ? activeOrCompletedLegs[activeOrCompletedLegs.length - 1] : legs[0];
};

const toOrderTrackingLanguage = (locale?: string): string => (locale ?? '').split('-')[0].toUpperCase();

const mapOrderTrackingDelivery = (
    delivery: IOrderTrackingDelivery,
    customerPO: string,
    postalCode: string,
    confirmedDate?: string
): IAnonymousDelivery => {
    const legs = delivery.Legs ?? [];
    const mainCarriageLeg = legs.find(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.MainCarriage) || legs[legs.length - 1];

    const preCarriageLegs = legs.filter(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.PreCarriage);
    const mainCarriageLegs = legs.filter(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.MainCarriage);
    const onCarriageLegs = legs.filter(leg => leg.LegType === DELIVERY_INFORMATION_LEG_TYPES.OnCarriage);

    const completedLegsIndices = legs
        .map((leg, index) =>
            leg.State === DELIVERY_INFORMATION_LEG_STATES.Active || leg.State === DELIVERY_INFORMATION_LEG_STATES.Completed ? index : -1
        )
        .filter(index => index !== -1);

    const allLegsCompleted = legs.length > 0 && legs.every(leg => leg.State === DELIVERY_INFORMATION_LEG_STATES.Completed);

    const activeStep = allLegsCompleted
        ? legs.length + 1
        : completedLegsIndices.length > 0
        ? completedLegsIndices[completedLegsIndices.length - 1] + 1
        : 0;

    const ordered = createSyntheticLeg(
        0,
        DELIVERY_INFORMATION_LEG_STATES.Ordered,
        DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.PARCEL,
        confirmedDate
    );
    const lastEventOverall = allLegsCompleted ? legs[legs.length - 1]?.Events?.slice(-1)[0] : undefined;
    const completed = createSyntheticLeg(
        legs.length + 1,
        'Completed',
        DELIVERY_INFORMATION_MODE_OF_TRANSPORTATIONS.FLAG,
        lastEventOverall?.Timestamp
    );

    const legToStep = (leg: IOrderTrackingLegs | ISyntheticLeg): IAnonymousDeliveryStep => {
        const lastEvent = leg.Events?.[leg.Events.length - 1];
        const status: AnonymousDeliveryStepStatus = leg.Id < activeStep ? 'completed' : leg.Id === activeStep ? 'active' : 'upcoming';

        return {
            stepKey: lastEvent?.Name || '',
            icon: legIcon(leg),
            status,
            timestamp: status !== 'upcoming' ? lastEvent?.Timestamp : undefined,
            location: status !== 'upcoming' ? lastEvent?.Location : undefined
        };
    };

    const steps: IAnonymousDeliveryStep[] = [
        ordered,
        findCurrentActiveLeg(preCarriageLegs),
        findCurrentActiveLeg(mainCarriageLegs),
        findCurrentActiveLeg(onCarriageLegs),
        completed
    ]
        .filter((leg): leg is IOrderTrackingLegs | ISyntheticLeg => Boolean(leg))
        .map(legToStep);

    const anonymousEvents: IAnonymousDeliveryEvent[] = legs
        .flatMap(leg => leg.Events ?? [])
        .map(event => ({ stepKey: event.Name, timestamp: event.Timestamp, location: event.Location }))
        .sort((eventA, eventB) => new Date(eventB.timestamp).getTime() - new Date(eventA.timestamp).getTime());

    const mainCarriage: IAnonymousDeliveryMainCarriage = {
        carrierName: mainCarriageLeg?.CarrierName || '',
        containerNumber: mainCarriageLeg?.Container || '',
        containerType: mainCarriageLeg?.ContainerType || '',
        shipNumber: mainCarriageLeg?.ShipFlightNumber || '',
        blNumber: mainCarriageLeg?.BlNumber || ''
    };

    return {
        postalCode,
        deliveryNumber: delivery.DeliveryNumber || '',
        customerPO,
        estimatedArrivalDate: delivery.EstimatedArrivalDate || '',
        steps,
        events: anonymousEvents,
        mainCarriage,
        liveTrackingAvailable: delivery.LiveTrackingAvailable,
        liveTrackingUrl: delivery.LiveTrackingUrl || ''
    };
};

const eventKey = (event: IOrderTrackingEvent): string => `${event.Name}|${event.Timestamp}|${event.Location ?? ''}`;

const mergeLegs = (target: IOrderTrackingLegs, source: IOrderTrackingLegs): IOrderTrackingLegs => {
    const events = new Map<string, IOrderTrackingEvent>();
    [...(target.Events ?? []), ...(source.Events ?? [])].forEach(event => {
        if (!events.has(eventKey(event))) {
            events.set(eventKey(event), event);
        }
    });
    const sortedEvents = Array.from(events.values()).sort(
        (eventA, eventB) => new Date(eventA.Timestamp).getTime() - new Date(eventB.Timestamp).getTime()
    );
    return { ...target, State: target.State ?? source.State, Events: sortedEvents };
};

const mergeDeliveries = (target: IOrderTrackingDelivery, source: IOrderTrackingDelivery): IOrderTrackingDelivery => {
    const legs = new Map<number, IOrderTrackingLegs>();
    [...(target.Legs ?? []), ...(source.Legs ?? [])].forEach(leg => {
        const existing = legs.get(leg.Id);
        legs.set(leg.Id, existing ? mergeLegs(existing, leg) : leg);
    });
    return {
        ...target,
        EstimatedArrivalDate: target.EstimatedArrivalDate || source.EstimatedArrivalDate,
        LiveTrackingAvailable: target.LiveTrackingAvailable || source.LiveTrackingAvailable,
        LiveTrackingUrl: target.LiveTrackingUrl || source.LiveTrackingUrl,
        Legs: Array.from(legs.values())
    };
};

const mergeDeliveriesByNumber = (deliveries: IOrderTrackingDelivery[]): IOrderTrackingDelivery[] => {
    const deliveryMap = new Map<string, IOrderTrackingDelivery>();
    deliveries.forEach(delivery => {
        if (!delivery.DeliveryNumber) {
            return;
        }
        const existing = deliveryMap.get(delivery.DeliveryNumber);
        deliveryMap.set(delivery.DeliveryNumber, existing ? mergeDeliveries(existing, delivery) : delivery);
    });
    return Array.from(deliveryMap.values());
};

const mapOrderTrackingToAnonymousDeliveries = (data: IPostAnonymousDeliveryData, postalCode: string): IAnonymousDelivery[] =>
    mergeDeliveriesByNumber(data.OrderTrackingData ?? []).map(delivery =>
        mapOrderTrackingDelivery(delivery, data.CustomerPONumber || '', postalCode, data.ConfirmedDate)
    );

async function action(input: PostAnonymousDeliveryInput, ctx: Msdyn365.IActionContext): Promise<IPostAnonymousDeliveryResponse> {
    const { trackingId, postalCode } = input;

    if (!trackingId) {
        return {
            success: false,
            message: 'Missing tracking ID.'
        };
    }

    if (!postalCode) {
        return {
            success: false,
            message: 'Please provide your postal code.'
        };
    }

    try {
        const response = await Msdyn365.sendRequest<IPostAnonymousDeliveryData>(
            `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetOrderTracking`,
            'post',
            {
                trackingNumber: trackingId,
                verificationCode: postalCode,
                language: toOrderTrackingLanguage(ctx.requestContext.locale)
            },
            {
                headers: {
                    OUN: ctx.requestContext.apiSettings.oun
                }
            }
        );

        if (response.status !== 200 || !response.data || response.data.Message !== OrderTrackingMessage.Success) {
            return {
                success: false,
                message: response.data?.Message
            };
        }

        const deliveries = mapOrderTrackingToAnonymousDeliveries(response.data, postalCode);

        if (deliveries.length === 0) {
            ctx.telemetry.warning(`GetOrderTracking returned no deliveries for ${trackingId}.`);
            return {
                success: false,
                message: OrderTrackingMessage.OrderNotFound
            };
        }

        return {
            success: true,
            deliveries
        };
    } catch (error) {
        console.error('GetOrderTracking failed.', error);
        return {
            success: false,
            message: 'Validation failed.'
        };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostAnonymousDeliveryResponse>>action,
    id: 'PostAnonymousDelivery',
    input: createInput
});
